import React, { useEffect, useState, useMemo, useRef } from "react";
import "./Dashboard.css";
import axios from "axios";
import OfflineDb from "../../db";
import { models } from "powerbi-client";
import DashboardRefresh from "./DashboardRefresh";
import DashboardDownload from "./DashboardDownload";
import { PowerBIEmbed } from "powerbi-client-react";
import ApprovalManagement from "./ApprovalManagement";
import { useActivityLogger } from "../../hooks/useActivityLogger";

const MonthlyReportDashboard = ({ reportDetails, reportKey }) => {
  const reportRef = useRef(null);
  const slicersUpdated = useRef(false);
  const slicersUpdatedCount = useRef(0);

  const [stateFilter, setStateFilter] = useState("All");
  const [facilityFilter, setFacilityFilter] = useState("All");
  const [periodFilter, setPeriodFilter] = useState("All");

  const { sessionUserBoValue } = useActivityLogger();

  const reportId = reportKey || 3;
  const email = sessionUserBoValue?.email || null;
  const id = sessionUserBoValue?.userCredentials?.id || null;

  const [reportInfo, setReportInfo] = useState(null);

  const baseURL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:9000/mrunner_api"
      : `${window.location.protocol}//${window.location.host}/mrunner_api`;

  const fetchData = async () => {
    try {
      const response = await axios.post(`${baseURL}/getPowerBIToken`, {
        r_id: reportId,
      });
      // console.log("powerBI token", response);
      setReportInfo(response.data);
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const embedConfig = useMemo(() => {
    const filters = [];

    if (email) {
      filters.push({
        $schema: "http://powerbi.com/product/schema#advanced",
        target: {
          table: "public dim_userrole_details",
          column: "username",
        },
        operator: "In",
        values: [email],
      });
    }
    if (id) {
      filters.push({
        $schema: "http://powerbi.com/product/schema#advanced",
        target: {
          table: "public dim_userrole_details",
          column: "uid",
        },
        operator: "In",
        values: [id],
      });
    }
    return {
      type: "report",
      id: reportInfo?.reportId,
      embedUrl: reportInfo?.embedUrl,
      tokenType: models.TokenType.Embed,
      accessToken: reportInfo?.finalToken,
      pageView: "actualSize",
      permissions: models.Permissions.All, // Or at least Read + Export
      settings: {
        filterPaneEnabled: false,
        navContentPaneEnabled: false,
        layoutType: models.LayoutType.FitToWidth,
        extensions: [
          {
            command: {
              name: "getData",
              title: "Get Data",
              extend: {
                visualContextMenu: {
                  title: "Get Data",
                  enabled: true,
                },
              },
            },
          },
        ],
      },
      filters,
    };
  }, [reportInfo, email, id]);

  const refreshDashboard = () => {
    slicersUpdated.current = false;
    setReportInfo(null);
    fetchData();
  };

  const getAllFilters = async () => {
    if (!reportRef.current) return;

    try {
      const pages = await reportRef.current.getPages();

      for (const page of pages) {
        const visuals = await page.getVisuals();
        const slicers = visuals.filter((visual) => visual.type === "slicer");
        slicers.forEach(async (slicer) => {
          const slicerState = await slicer.getSlicerState();
          slicerState.filters.forEach((f) => {
            if (f.target.column === "State") {
              setStateFilter(f.values.join(","));
            }
            if (f.target.column === "Facility") {
              setFacilityFilter(f.values.join(","));
            }
            if (
              f.target.column === "MonthYear" ||
              f.target.column === "YearMonth"
            ) {
              setPeriodFilter(f.values.join(","));
            }
          });
        });
      }
    } catch (error) {
      console.error("Error getting filters:", error);
    }
  };

  const setSlicers = async (orgDetails) => {
    if (!reportRef.current) return;

    try {
      if (slicersUpdatedCount.current >= 10) {
        slicersUpdated.current = true;
      } else {
        slicersUpdatedCount.current += 1;
      }
      const pages = await reportRef.current.getPages();
      let state = null;
      for (const page of pages) {
        const visuals = await page.getVisuals();
        // console.log("visuals", visuals);
        const slicers = visuals.filter((visual) => visual.type === "slicer");
        const kpiCard = visuals.find(
          (v) => v.type === "cardVisual" && v.title === "Name of the State"
        );

        if (kpiCard) {
          const data = await kpiCard.exportData(
            models.ExportDataType.Summarized
          );
          if (data) {
            const csv = data.data; // CSV string
            const lines = csv.trim().split("\n");
            const headers = lines[0].split(",");
            const values = lines[1].split(",");

            console.log(`Card value (${headers[0]}): ${values[0]}`);
            state = values[0];
          }
        }

        // console.log("kpiCards", kpiCard);
        // console.log("state", state);

        for (const slicer of slicers) {
          const slicerState = await slicer.getSlicerState();
          // Determine which slicer we're working with
          const columnName = slicerState?.targets[0]?.column;
          const tableName = slicerState?.targets[0]?.table;

          // console.log("reportDetails", reportDetails);
          const sValue = reportDetails?.state || state;
          // console.log("sValue", sValue);
          const fValue = reportDetails?.facility || orgDetails?.name || null;
          // console.log("fValue", fValue);
          // Set different values based on the slicer type
          if (columnName === "State" && sValue) {
            await slicer.setSlicerState({
              filters: [
                {
                  $schema: "http://powerbi.com/product/schema#basicFilter",
                  target: {
                    table: tableName,
                    column: columnName,
                  },
                  filterType: 1, // Basic filter
                  operator: "In",
                  values: [sValue], // Your default values here
                },
              ],
            });
          } else if (columnName === "Facility" && fValue) {
            await slicer.setSlicerState({
              filters: [
                {
                  $schema: "http://powerbi.com/product/schema#basicFilter",
                  target: {
                    table: tableName,
                    column: columnName,
                  },
                  filterType: 1,
                  operator: "In",
                  values: [fValue], // Your default values here
                },
              ],
            });
          } else if (
            (columnName === "MonthYear" || columnName === "YearMonth") &&
            reportDetails &&
            reportDetails.period !== "All"
          ) {
            await slicer.setSlicerState({
              filters: [
                {
                  $schema: "http://powerbi.com/product/schema#basicFilter",
                  target: {
                    table: tableName,
                    column: columnName,
                  },
                  filterType: 1,
                  operator: "In",
                  values: [reportDetails.period], // Your default values here
                },
              ],
            });
          }
        }
      }
    } catch (error) {
      console.error("Error getting filters:", error);
    }
  };

  return (
    <div
      style={{
        padding: reportDetails ? "0px" : "65px 0px 0px",
        height: "calc(100vh - 64px)",
        width: "100%",
        position: "relative",
      }}
    >
      {reportInfo?.bearerToken && !reportDetails && (
        <div
          className="text-right"
          style={{
            padding: "5px",
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          <DashboardRefresh
            reportId={reportId}
            accessToken={reportInfo?.bearerToken}
            groupId={reportInfo?.workspaceId}
            datasetId={reportInfo?.datasetId}
            refreshDashboard={refreshDashboard}
          />

          {email && periodFilter && periodFilter !== "All" && (
            <DashboardDownload
              email={email}
              periodFilter={periodFilter}
              type="R1"
            ></DashboardDownload>
          )}
          <ApprovalManagement
            stateFilter={stateFilter}
            periodFilter={periodFilter}
            facilityFilter={facilityFilter}
          />
        </div>
      )}
      {reportInfo && (
        <PowerBIEmbed
          embedConfig={embedConfig}
          getEmbeddedComponent={(embeddedReport) => {
            reportRef.current = embeddedReport;
          }}
          eventHandlers={
            new Map([
              [
                "loaded",
                function () {
                  if (!reportDetails) {
                    // console.log("Report loaded");
                    getAllFilters();
                  }
                },
              ],
              [
                "rendered",
                async function () {
                  if (!reportDetails) {
                    // console.log("Report rendered normal");
                    getAllFilters();
                  }
                  if (!slicersUpdated.current) {
                    let orgDetails = await OfflineDb.getDataFromPouchDB(
                      "orgDetails"
                    );
                    // console.log("orgDetails", orgDetails.data);
                    // console.log("slicersUpdated", slicersUpdated.current);
                    // console.log("Report rendered from approval");
                    setSlicers(reportDetails?.facility || orgDetails.data);
                  }
                },
              ],
              [
                "filtersApplied",
                function () {
                  if (!reportDetails) {
                    // console.log("Filters changed by user normal");
                    getAllFilters();
                  }
                },
              ],
              [
                "error",
                function (event) {
                  // console.log("Power BI error:", event.detail);
                },
              ],
            ])
          }
          cssClassName="powerbi-container"
        />
      )}
    </div>
  );
};

export default MonthlyReportDashboard;
